import { useRouter } from 'next/router';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { trackMixpanelEvent } from 'utils/analytics';
import { UserHasPermission } from 'utils/auth/roles-and-permissions';

import { OnboardingCheckerItem } from '@/modules/Onboarding/components/SetupGuide/OnboardingCheckerItem';
import { useCreateEditContractUtils } from '@/modules/workforce/Contracts/components/CreateAndEdit/logic';
import { AddEditCrew } from '@/modules/workforce/Crew/components/Modals/AddEditCrew';
import { useCreateTechnicalOperationUtils } from '@/modules/workingDay/components/CreateTechnicalOperation/logic';
import { Collapse } from '@/shared/components/atoms';
import ConfirmationModal from '@/shared/components/molecules/ConfirmationModal';
import { permissions } from '@/shared/constant/roles-and-permissions';
import { useAuth } from '@/shared/hooks/context/auth';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

const WorkforceCollapse = () => {
  const { openModal, closeModal } = useModal();
  const { onboardingChecks, user } = useAuth();
  const { locale } = useRouter();
  const { t } = useTranslation(['onboarding', 'common']);
  const { openCreateTechnicalOperationModal } =
    useCreateTechnicalOperationUtils();
  const { openCreateContractModal } = useCreateEditContractUtils();

  const handleAddCrew = useCallback(() => {
    trackMixpanelEvent('Onboarding_start_add_crews_and_workers', user);
    openModal(<AddEditCrew />, {
      variant: 'full',
      title: t('add-crew', { ns: 'common' }),
      titleVariant: 'h6',
      onClose: () =>
        openModal(
          <ConfirmationModal
            header={t('going-somewhere-else', { ns: 'common' })}
            message={t('you-will-lose-all-data', { ns: 'common' })}
            variant='warning'
            dismissText={t('continue-editing', { ns: 'common' })}
            confirmText={t('discard-and-exit', { ns: 'common' })}
            onDismissRemoveNumber={1}
            onConfirm={closeModal}
            buttonsAlignment='col'
            preferredAction='dismiss'
          />
        ),
    });
  }, [openModal, t, user, closeModal]);

  const handleCreateContract = useCallback(() => {
    trackMixpanelEvent('Onboarding_start_create_contracts', user);
    openCreateContractModal();
  }, [openCreateContractModal, user]);

  const handleCreateTechnicalOperation = useCallback(() => {
    trackMixpanelEvent('Onboarding_start_create_technical_operation', user);
    openCreateTechnicalOperationModal();
  }, [openCreateTechnicalOperationModal, user]);

  return (
    <Collapse
      defaultValue
      bgColor='bg-background-neutral-99'
      childrenClassName='flex flex-col gap-16'
      header={<div className='font-semi-bold'>{t('workforce')}</div>}
    >
      <OnboardingCheckerItem
        action={handleAddCrew}
        description={t('add-your-crews-workers-description')}
        status={onboardingChecks?.hasCrews ? 'done' : 'idle'}
        stepNumber={1}
        isDisabled={
          !UserHasPermission({
            role: user?.userGroupId,
            action: permissions.CAN_ADD_SUPPLIER,
          })
        }
        title={t('add-your-crews-workers')}
        moreUrl={
          locale === 'es'
            ? 'https://aydi.helpjuice.com/es_ES/funciones/add-crew'
            : locale === 'fr'
            ? 'https://aydi.helpjuice.com/fr_FR/crear-equipo-de-trabajadores'
            : locale === 'ar'
            ? 'https://aydi.helpjuice.com/ar_SA/add-crew'
            : 'https://aydi.helpjuice.com/en_US/workforce-module-/add-crew'
        }
      />
      <OnboardingCheckerItem
        action={handleCreateContract}
        description={t('create-contract-description')}
        status={onboardingChecks?.hasContract ? 'done' : 'idle'}
        stepNumber={2}
        title={t('create-contract')}
        isDisabled={
          !UserHasPermission({
            role: user?.userGroupId,
            action: permissions.CAN_VIEW_CONTRACTS,
          })
        }
        moreUrl={
          locale === 'es'
            ? 'https://aydi.helpjuice.com/es_ES/creacion-contratos-de-trabajo'
            : locale === 'fr'
            ? 'https://aydi.helpjuice.com/fr_FR/creacion-contratos-de-trabajo'
            : locale === 'ar'
            ? 'https://aydi.helpjuice.com/ar_SA/creacion-contratos-de-trabajo'
            : 'https://aydi.helpjuice.com/en_US/configurar-tu-cuenta/creacion-contratos-de-trabajo'
        }
      />
      <OnboardingCheckerItem
        action={handleCreateTechnicalOperation}
        description={t('create-technical-operation-description')}
        status={onboardingChecks?.hasTechnicalOperation ? 'done' : 'idle'}
        stepNumber={3}
        isDisabled={
          !UserHasPermission({
            role: user?.userGroupId,
            action: permissions.CAN_ADD_TECHNICAL_OPERATION,
          })
        }
        title={t('create-technical-operation')}
        moreUrl={
          locale === 'es'
            ? 'https://aydi.helpjuice.com/es_ES/funciones/assign-technical-operation'
            : locale === 'ar'
            ? 'https://aydi.helpjuice.com/ar_SA/assign-technical-operation'
            : 'https://aydi.helpjuice.com/en_US/workforce-module-/assign-technical-operation'
        }
      />
    </Collapse>
  );
};

export { WorkforceCollapse };
